import { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, ButtonBase } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import palette from "../../../../utilities/palette";
import { TextField } from "../../formFields/TextField";
import { FlexColumnDiv, FlexRowDiv, StepperContentBodyDiv } from "../../../styledComponents/Components.styles";
import LabelField from "../../formFields/LabelField";
import { CommonButton } from "../../../styledComponents/MUI.styles";
import { RemoveButton } from "../../../styledComponents/Buttons.styles";
import { textHints } from "../../../../utilities/hintText";
import { extractSelectionCriteriaErrors } from "../../../../utilities/validationErrorExtraction";



export const SelectionCriteria = ({
    moduleInformation,
    machineList, setMachineList, machineIndex,
    machineListErrors, setMachineListErrors,
    variantIndex,
    clusterIndex,
}) => {

    // tracks/handles what accordion element is expanded
    const [expanded, setExpanded] = useState(``);

    const handleChange = (panel) => (_, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const [fieldsWithErrorList, setFieldsWithErrorList] = useState([]);

    useEffect(() => {
        const { fieldsWithErrorList } = extractSelectionCriteriaErrors(
            machineListErrors,
            machineIndex,
            variantIndex,
            clusterIndex
        );
        
        setFieldsWithErrorList(fieldsWithErrorList);
    }, [machineListErrors, machineIndex, variantIndex, clusterIndex]);

    // Generic onChange handler for TextField components
    const handleFieldChange = (criteriaIndex, fieldName) => (e) => {
        setMachineList((prevState) => {
            const newState = structuredClone(prevState);
            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].selectionCriteriaList[criteriaIndex][fieldName] = e.target.value;
            return newState;
        });
    };

    // Derive toBeRenderedObject directly during render instead of storing in state
    const selectionCriteriaList = machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].selectionCriteriaList;

    const toBeRenderedObject = {} as Record<string, number[]>;
    selectionCriteriaList.forEach((criteria, criteriaIndex) => {
        const key = `${criteria.modelYear}-${criteria.programCode}`;
        if (!toBeRenderedObject[key]) {
            toBeRenderedObject[key] = []
        }
        toBeRenderedObject[key].push(criteriaIndex)
    });    

    const handleRemoveCriteria = (e, criteriaIndex) => {
             e.preventDefault();
            setMachineList(
                (prevState) => {
                    const newState = structuredClone(prevState);
                    newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].selectionCriteriaList.splice(criteriaIndex, 1);
                    return newState;
                }
            );
            setMachineListErrors(
                (prevState) => {
                    const newState = structuredClone(prevState);
                    newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].selectionCriteriaListErrors.splice(criteriaIndex, 1);
                    return newState;
                }
            );
    }

    const handleAddCriteria = (e, programCode, modelYear) => {
        e.preventDefault();
        setMachineList(
            (prevState) => {
                const newState = structuredClone(prevState);
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].selectionCriteriaList.push({
                    programCode: programCode,
                    modelYear: modelYear,
                    ncPartNumber: "",
                    mfalCode: "",
                    did: ""
                });
                return newState;
            }
        );
        setMachineListErrors(
            (prevState) => {
                const newState = structuredClone(prevState);
                newState[machineIndex].variantListErrors[variantIndex].clusterListErrors[clusterIndex].selectionCriteriaListErrors.push({});
                return newState;
            }
        );
    }

    
    return (
        <>  
            <div style={{color: palette.main, fontWeight: '600', fontSize: '20px', margin: '20px 0'}}>Selection Criteria Details</div>

            {moduleInformation.vehiclePrograms.map((key, index) => {
                const [modelYear, programCode] = key.split('-');
                const criteriaIndices = toBeRenderedObject[key];

                return (
                    <div 
                        style={{marginBottom: '20px', marginLeft: '5px'}}
                        key={`selectCriteria_${modelYear}_${programCode}`}
                        >
                        <Accordion 
                            slotProps={{ transition: { unmountOnExit: true } }}
                            expanded={expanded === `panel${machineIndex}-${variantIndex}-${clusterIndex}-${index}`} 
                            onChange={handleChange(`panel${machineIndex}-${variantIndex}-${clusterIndex}-${index}`)}
                            id={`panel${machineIndex}-${variantIndex}-${clusterIndex}-${index}`}
                            sx={{ 
                                "::before": {
                                    display: 'none'
                                },
                                background: 'transparent',
                                padding: '20px 0',
                                boxShadow: 'none',
                            }}
                        >
                        {/* Top bar of accordion element */}
                        <ButtonBase sx={{width: '100%'}}>
                            <AccordionSummary
                                aria-controls={`panel${machineIndex}-${variantIndex}-${clusterIndex}-${index}-content`}
                                id={`panel${machineIndex}-${variantIndex}-${clusterIndex}-${index}-header`}
                                sx={{
                                    ":hover": {
                                        backgroundColor: palette.header.main
                                    },
                                    borderRadius: '10px'
                                }}
                            >
                                {expanded === `panel${machineIndex}-${variantIndex}-${clusterIndex}-${index}` && <ExpandMoreIcon sx={{fontSize: '30px'}}/>}
                                    <div style={{ display: "flex", flexDirection: "column", alignSelf: "center", width: "100%", cursor: 'pointer' }}>
                                        <FlexColumnDiv style={{
                                            borderLeft: `4px solid ${palette.main}`,
                                            borderRadius: '4px',
                                            minHeight: '100px',
                                            padding: '10px 20px 10px 20px',
                                            gap: '5px',
                                        }}>
                                            <span style={{ fontWeight: "650", fontSize: "20px", paddingLeft: '5px', color: palette.main }}>
                                                Selection Criteria {`${index + 1}`}
                                            </span>

                                            <FlexRowDiv style={{gap: '200px'}}>
                                                <LabelField
                                                    label="Program Code"
                                                    value={programCode ?? "N/A"}
                                                />
                                                <LabelField
                                                    label="Model Year"
                                                    value={modelYear ?? "N/A"}
                                                />
                                            </FlexRowDiv>
                                            {fieldsWithErrorList[index] && fieldsWithErrorList[index].length > 0 &&
                                                <span style={{color: palette.stepper.error, paddingTop: '5px', marginLeft: '5px'}}>Validation errors found in one or more input fields</span>
                                            }
                                        </FlexColumnDiv>                                
                                    </div>
                                
                            </AccordionSummary>
                        </ButtonBase>

                        <AccordionDetails>
                            <FlexColumnDiv>
                                {criteriaIndices.map((criteriaIndex, idx) => (
                                    <StepperContentBodyDiv key={`criteriaEntry_${criteriaIndex}`} style={{width: '1200px', alignSelf: 'center', gap: '20px', marginBottom: '20px', flexDirection: 'column'}}>
                                        <FlexRowDiv style={{width: '100%', justifyContent: 'space-between', alignItems: 'center'}}>
                                        <TextField
                                            label="Non-Connected Part Number"
                                            value={machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].selectionCriteriaList[criteriaIndex].ncPartNumber}
                                            onChange={handleFieldChange(criteriaIndex, 'ncPartNumber')}
                                            placeholder="Enter Non-Connected Part Number"
                                            textHint={textHints.ncPartNumber}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.selectionCriteriaList.0.ncPartNumber`}
                                            stylingVariant="body"
                                        />

                                        <RemoveButton style={{alignSelf: 'start'}}
                                            disabled={criteriaIndices.length <= 1}
                                            onClick={(e) => handleRemoveCriteria(e, criteriaIndex)}
                                        >
                                            X
                                        </RemoveButton>
                                        </FlexRowDiv>

                                        <TextField
                                            label="MFAL Code"
                                            value={machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].selectionCriteriaList[criteriaIndex].mfalCode}
                                            onChange={handleFieldChange(criteriaIndex, 'mfalCode')}
                                            placeholder="Enter MFAL"
                                            textHint={textHints.mfalCode}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.selectionCriteriaList.0.mfalCode`}
                                            stylingVariant="body"
                                        />
                                        
                                        {/* did */}
                                        <TextField
                                            label="DID"
                                            value={machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].selectionCriteriaList[criteriaIndex].did}
                                            onChange={handleFieldChange(criteriaIndex, 'did')}
                                            placeholder="Enter DID"
                                            textHint={textHints.did}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.selectionCriteriaList.0.did`}
                                            stylingVariant="body"
                                        />
                                    </StepperContentBodyDiv>
                                ))}

                                <CommonButton
                                    sx={{width: '250px', height: '37px', alignSelf: 'end'}}
                                    onClick={(e) => handleAddCriteria(e, programCode, modelYear)}
                                >
                                    Add Selection Criteria
                                </CommonButton>

                            </FlexColumnDiv>
                        </AccordionDetails>
                                            
                        </Accordion>
                    </div>
                )
            })
            }
        </>
    )
}